import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import toast from "../sweetalert/Sweetalert";
import './view.scss';
import Swal from "sweetalert2";
import { useSidebarData } from "../../hooks/sidebarData";
import { useDtrData } from "../../hooks/dtrData";

const Dtr = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    
    const { setSidebarData } = useSidebarData();
    const { setDtrData } = useDtrData();

    const timeoutRef = useRef(null);

    const params = new URLSearchParams(window.location.search);
    const employeeid = params.get('employeeid');
    const department = params.get('department');
    const departmentcode = params.get('departmentcode');
    const payperiod = params.get('payperiod');
    const payroll = params.get('payroll');

    const accesstoken = sessionStorage.getItem('accesstoken');
    const name = sessionStorage.getItem('name');
    const loggedUname = sessionStorage.getItem('uname');
    const loggedDepartment = sessionStorage.getItem('departmentslug');
    const employeeName = sessionStorage.getItem('timesheetEmployee');
    const uname = sessionStorage.getItem('timesheetEmployeeUname');
    const currentPayperiod = sessionStorage.getItem('timesheetPayperiodId');

    const [loading, setLoading] = useState(false);
    const [activeDTRCell, setActiveDTRCell] = useState(null);

    const dtrStatusKey = ['dtrstatus', { uname, currentPayperiod }];

    const [managedDepartment, setManagedDepartment] = useState(false);

    const { data: role } = useQuery({
        queryKey: ['userrole']
    });

    const { data: usermanageddepartments } = useQuery({
        queryKey: ['managedDepartments', uname]
    });

    useEffect(() => {
        const checkDepartment = usermanageddepartments?.find(dept => dept.department_code === loggedDepartment);
        setManagedDepartment(checkDepartment);
    }, [usermanageddepartments])

    const { data: dtrstatus, isLoading: isLoadingDTRStatus, error: errorDtrStatus } = useQuery({
        queryKey: dtrStatusKey,
        queryFn: async () => {
            const res = await axios.get(`dtr/status?uname=${uname}&payperiod=${currentPayperiod}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!uname && !!currentPayperiod,
    });

    useEffect(() => {
        if (errorDtrStatus?.response?.data.status === 'inactive') {
            handleSessionExpire();
        }
    }, [errorDtrStatus]);

    const handleSessionExpire = () => {
        destroySession();
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
    };

    const { data: dtr, isLoading: isLoadingDTR, error: errorDTR } = useQuery({
        queryKey: ['userDTR', { uname, currentPayperiod }],
        queryFn: async () => {
            const res = await axios.get(`dtr/users/${uname}?payperiod=${currentPayperiod}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!uname && !!currentPayperiod,
    });

    useEffect(() => {
        if (errorDTR) {
            if (errorDTR.response?.data.status === 'inactive') {
                handleSessionExpire();
            } else {
                toast.fire({
                    icon: 'error',
                    title: errorDTR.response?.data.response || 'Something went wrong while fetching user timesheet!'
                });
            }
        }
    }, [errorDTR]);

    const { data: approver } = useQuery({
        queryKey: ['approverdetails'],
        queryFn: async () => {
            const res = await axios.get(`dtr/timesheets/approver?uname=${loggedUname}&department=${loggedDepartment}&payperiod=${currentPayperiod}&employee=${uname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        enabled: !!loggedUname && !!loggedDepartment && !!currentPayperiod && !!uname && !!accesstoken,
        retry: 1
    });

    const updateUserDtrStatusMutation = useMutation({
        mutationFn: async (statusData) => {
            const res = await axios.patch(`dtr/timesheets`, statusData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            setLoading(false);
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(dtrStatusKey);
        },
        onError: (error) => {
            setLoading(false);
            const errorMessage = error.response?.data.response || 'Something went wrong while updating user DTR status!';
            toast.fire({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                handleSessionExpire();
            }
        }
    });

    const handleDtrApprovalDisapproval = (status) => {
        Swal.fire({
            text: `Are you sure you want to ${status === 1 ? 'APPROVE' : 'DISAPPROVE'} this timesheet?`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async (result) => {
                if (result) {
                    setLoading(true);
                    await updateUserDtrStatusMutation.mutateAsync({
                        status,
                        uname,
                        name,
                        payperiod: currentPayperiod,
                        department: loggedDepartment,
                        updatorID: approver?.approver_id,
                        updator: loggedUname,
                        approvalID: approver?.approval_id,
                        submissionID: approver?.submission_id
                    });
                }
            }
        });
    };

    const handleAdjustmentRequest = (id) => {
        setSidebarData('state', true);
        setSidebarData('key', 'manager-record-adjustment');
        setSidebarData('label', 'Record Adjustment');
        setDtrData('payperiod', currentPayperiod);
        setDtrData('departmentcode', departmentcode);
        setActiveDTRCell(id);

        const cachedItems = queryClient.getQueryData(['userDTR', { uname, currentPayperiod }]);
        if (cachedItems) {
            const item = cachedItems.find(i => i.id === id);
            if (item) {
                setSidebarData('data', item);
            }
        }
    };

    const handleTimesheetApprovalStatus = () => {
        setSidebarData('state', true);
        setSidebarData('key', 'dtr-approval');
        setSidebarData('label', 'Timesheet Approval Status');
        setDtrData('employee', uname);
        setDtrData('payperiod', currentPayperiod);
        setDtrData('departmentcode', departmentcode);
    }

    return (
        <div className="view">
            <table className={isLoadingDTR || isLoadingDTRStatus ? 'loading' : ''}>
                <thead>
                    <tr>
                        <th colSpan="12" onClick={handleTimesheetApprovalStatus}>TIMESHEET</th>
                    </tr>
                    <tr>
                        <td colSpan="12" className="status">
                            {dtrstatus === null ? null : (
                                (dtrstatus === 0 || dtrstatus === 1) ? (
                                    <div>
                                    
                                        {(approver?.uname === loggedUname && loggedUname !== uname) && (<>
                                            <button type="button" className={`approve ${(isLoadingDTRStatus || loading) && ' loading'}`} onClick={() => handleDtrApprovalDisapproval(1)}>
                                                APPROVE
                                            </button>
                                            <button type="button" className={`disapprove ${(isLoadingDTRStatus || loading) && ' loading'}`} onClick={() => handleDtrApprovalDisapproval(2)}>
                                                DISAPPROVE
                                            </button>
                                        </>)}
                                        
                                        <h3 className={`pending ${(isLoadingDTRStatus || loading) && ' hide'}`}>
                                            Pending
                                        </h3>
                                    </div>
                                ) : (
                                    dtrstatus === 2 ? (
                                        <h3 className={`approved ${(isLoadingDTRStatus || loading) && ' hide'}`}>
                                            Approved
                                        </h3>
                                    ) : (
                                        <div>
                                            {(approver?.uname === loggedUname && loggedUname !== uname) && (<>
                                                <button type="button" className={`approve ${(isLoadingDTRStatus || loading) && ' loading'}`} onClick={() => handleDtrApprovalDisapproval(1)}>
                                                    APPROVE
                                                </button>
                                                <button type="button" className={`disapprove ${(isLoadingDTRStatus || loading) && ' loading'}`} onClick={() => handleDtrApprovalDisapproval(2)}>
                                                    DISAPPROVE
                                                </button>
                                            </>)}

                                            <h3 className={`disapproved ${(isLoadingDTRStatus || loading) && ' hide'}`}>
                                                Disapproved
                                            </h3>
                                        </div>
                                    )
                                )
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th colSpan="3">PayPeriod</th>
                        <th colSpan="3">{payperiod}</th>
                        <th colSpan="3">Payroll Date</th>
                        <th colSpan="3">{payroll}</th>
                    </tr>
                    <tr>
                        <th colSpan="3">Employee ID</th>
                        <th colSpan="3">{employeeid === 'null' ? '' : employeeid}</th>
                        <th colSpan="3">Company</th>
                        <th colSpan="3">OCVI-MSW</th>
                    </tr>
                    <tr>
                        <th colSpan="3" rowSpan="2">Employee Name</th>
                        <th colSpan="3" rowSpan="2">{employeeName}</th>
                        <th colSpan="3">Department</th>
                        <th colSpan="3">{department}</th>
                    </tr>
                    <tr>
                        <th colSpan="3">Unit / Group</th>
                        <th colSpan="3">Operations</th>
                    </tr>
                    <tr>
                        <th>Day</th>
                        <th>Shift Schedule</th>
                        <th>Time In</th>
                        <th>Time Out</th>
                        <th>Start Break</th>
                        <th>Stop Break</th>
                        <th>Total Break</th>
                        <th>Total Hours</th>
                        <th>Undertime</th>
                        <th>Overbreak</th>
                        <th>ND</th>
                        <th>OT</th>
                    </tr> 
                </thead>
                <tbody>
                    {isLoadingDTR ? (
                        [...Array(10)].map((_, i) => (
                            <tr key={i}>
                                {Array.from({ length: 12 }, (_, j) => <td key={j}><span></span></td>)}
                            </tr>
                        ))
                    ) : (
                        dtr?.map((item) => (
                            <tr 
                                key={item.id}
                                className={(role === 'manager' || role === 'supervisor' || ((role === 'admin' || role === 'management') && departmentcode === loggedDepartment)) ? (activeDTRCell === item.id ? `${item.class} active` : item.class) : null}
                                onClick={() => {
                                    if ((role === 'manager' || role === 'supervisor' || ((role === 'admin' || role === 'management') && departmentcode === loggedDepartment)) && item.class.includes('adjustment')) {
                                        handleAdjustmentRequest(item.id);
                                    }
                                }}
                            >
                                <td><span>{item.day}</span></td>
                                <td><span>{item.shift}</span></td>
                                <td><span>{item.timein}</span></td>
                                <td><span>{item.timeout}</span></td>
                                <td><span>{item.startbreak}</span></td>
                                <td><span>{item.stopbreak}</span></td>
                                <td><span>{item.totalbreak}</span></td>
                                <td><span>{item.totalhours}</span></td>
                                <td><span>{item.undertime}</span></td>
                                <td><span>{item.overbreak}</span></td>
                                <td><span>{item.nd}</span></td>
                                <td><span>{item.ot}</span></td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default Dtr;
